// GruposView — lista, cria e edita grupos administrativos.
//
// Funcionalidades:
//   - Lista com busca, filtro por company, count de members e bancos
//   - Modal de criação (nome, descrição, company opcional, tags, members iniciais)
//   - Modal de edição (todos os campos + gestão de members via add/remove)
//   - Ação "Aplicar tags aos membros" — propaga tags do grupo nos users (merge)
//   - Delete com confirmação
//
// Bancos da turma são informacionais (referência manual do admin) — a fonte
// da verdade de acesso continua sendo isPublicToCompany + memberships.

import { signal } from "@preact/signals";
import { useEffect, useMemo, useState } from "preact/hooks";
import {
  AlertCircle,
  Building2,
  Database,
  GraduationCap,
  Link2,
  Loader2,
  Pencil,
  Plus,
  Save,
  Search,
  Sparkles,
  Tag,
  Trash2,
  UserPlus,
  Users,
  UsersRound,
  X
} from "lucide-react";
import { Checkbox } from "@app/ui/components/checkbox";
import { Skeleton } from "@app/ui/components/skeleton";
import {
  api,
  ApiError,
  type BankListItem,
  type CompanyMin,
  type GroupDetail,
  type GroupListRow,
  type UserRow
} from "./api";
import { GroupBanksModal } from "./GroupBanksSection";
import { GroupMembersModal } from "./GroupMembersSection";
import { openAlert } from "./ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";

const groupList = signal<GroupListRow[] | null>(null);
const companies = signal<CompanyMin[]>([]);
const allUsers = signal<UserRow[] | null>(null);
const allBanks = signal<BankListItem[]>([]);
const phase = signal<"loading" | "ready" | "error">("loading");
const errMsg = signal<string | null>(null);
const search = signal<string>("");
const filterCompany = signal<string>("all");
const editing = signal<GroupDetail | null>(null);
const banksPanel = signal<GroupDetail | null>(null);
const membersPanel = signal<GroupDetail | null>(null);
const creating = signal<boolean>(false);
const deleting = signal<GroupListRow | null>(null);

async function load() {
  phase.value = "loading";
  errMsg.value = null;
  try {
    const [gres, ures, bres] = await Promise.all([
      api.listGroups(),
      api.listUsers(),
      api.listBanks()
    ]);
    groupList.value = gres.groups;
    companies.value = ures.companies;
    allUsers.value = ures.users;
    allBanks.value = bres.banks;
    phase.value = "ready";
  } catch (err) {
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    errMsg.value = friendlyError(err);
    phase.value = "error";
  }
}

async function openEdit(id: string) {
  try {
    const res = await api.getGroup(id);
    editing.value = res.group;
  } catch (err) {
    void openAlert({ title: "Erro", message: friendlyError(err) });
  }
}

async function openBanks(id: string) {
  try {
    const res = await api.getGroup(id);
    banksPanel.value = res.group;
  } catch (err) {
    void openAlert({ title: "Erro", message: friendlyError(err) });
  }
}

async function openMembers(id: string) {
  try {
    const res = await api.getGroup(id);
    membersPanel.value = res.group;
  } catch (err) {
    void openAlert({ title: "Erro", message: friendlyError(err) });
  }
}

export function GruposView() {
  useEffect(() => {
    load();
  }, []);
  if (phase.value === "loading") return <GroupsLoading />;
  if (phase.value === "error") return <GroupsError />;
  return (
    <>
      <GroupsReady />
      {editing.value && <EditGroupModal group={editing.value} />}
      {creating.value && <CreateGroupModal />}
      {deleting.value && <DeleteGroupModal group={deleting.value} />}
      {banksPanel.value && (
        <GroupBanksModal
          group={banksPanel.value}
          allBanks={allBanks.value}
          onClose={() => {
            banksPanel.value = null;
            load();
          }}
        />
      )}
      {membersPanel.value && (
        <GroupMembersModal
          group={membersPanel.value}
          allUsers={allUsers.value ?? []}
          onClose={() => {
            membersPanel.value = null;
            load();
          }}
        />
      )}
    </>
  );
}

function GroupsLoading() {
  return (
    <div class="space-y-3">
      <Skeleton className="h-12 rounded-md" />
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} className="h-14 rounded-md" />
      ))}
    </div>
  );
}

function GroupsError() {
  return (
    <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3">
      <div class="flex items-start gap-2">
        <AlertCircle className="mt-0.5 size-5 shrink-0 text-destructive" />
        <div>
          <h3 class="text-sm font-bold">Falha ao listar turmas</h3>
          {errMsg.value && <p class="text-xs text-muted-foreground">{errMsg.value}</p>}
          <button
            type="button"
            onClick={() => load()}
            class="mt-2 rounded-md border border-border bg-background px-3 py-1 text-xs font-semibold hover:bg-accent"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    </div>
  );
}

function GroupsReady() {
  const rows = groupList.value ?? [];
  const q = search.value.toLowerCase().trim();
  const fc = filterCompany.value;
  const visible = useMemo(() => {
    return rows.filter((g) => {
      if (fc === "platform" && g.companyId !== null) return false;
      if (fc !== "all" && fc !== "platform" && g.companyId !== fc) return false;
      if (q) {
        const hay = `${g.name} ${g.description ?? ""} ${g.tags.join(" ")} ${
          g.companyName ?? ""
        } ${g.createdByEmail ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, q, fc]);

  return (
    <div class="space-y-4">
      {/* Toolbar */}
      <div class="flex flex-wrap items-center gap-2 rounded-md border border-border bg-card/40 px-3 py-2">
        <div class="relative min-w-[240px] flex-1">
          <Search
            width="1em"
            height="1em"
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="search"
            placeholder="Buscar nome, descrição, tag, organização…"
            value={search.value}
            onInput={(e) => (search.value = (e.target as HTMLInputElement).value)}
            class="w-full rounded-md border border-border bg-background pl-8 pr-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </div>
        <select
          value={filterCompany.value}
          onChange={(e) => (filterCompany.value = (e.target as HTMLSelectElement).value)}
          class="rounded-md border border-border bg-background px-2 py-1.5 text-xs"
        >
          <option value="all">Todas as organizações</option>
          <option value="platform">Só plataforma (sem organização)</option>
          {companies.value.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => (creating.value = true)}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110"
        >
          <Plus width="1.1em" height="1.1em" />
          Nova turma
        </button>
      </div>

      <p class="px-1 text-xs text-muted-foreground">
        {visible.length} de {rows.length} turma{rows.length !== 1 ? "s" : ""}
      </p>

      {visible.length === 0 ? (
        <div class="rounded-md border border-dashed border-border bg-card/30 px-4 py-8 text-center text-sm text-muted-foreground">
          Nenhuma turma encontrada.
        </div>
      ) : (
        <div class="overflow-hidden rounded-md border border-border bg-card/40">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-border bg-card/60 text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                <th class="px-3 py-2">Turma</th>
                <th class="px-3 py-2">Tipo / Dono</th>
                <th class="px-3 py-2">Organização</th>
                <th class="px-3 py-2">Membros</th>
                <th class="px-3 py-2">Bancos</th>
                <th class="px-3 py-2">Tags</th>
                <th class="px-3 py-2 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((g) => (
                <GroupListRowEl key={g.id} group={g} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function GroupListRowEl({ group }: { group: GroupListRow }) {
  return (
    <tr class="border-b border-border/60 transition-colors last:border-0 hover:bg-accent/30">
      <td class="px-3 py-2">
        <div class="font-semibold">{group.name}</div>
        {group.description && (
          <div class="line-clamp-1 text-[11px] text-muted-foreground">
            {group.description}
          </div>
        )}
      </td>
      <td class="px-3 py-2">
        {group.ownerUserId ? (
          <div class="flex flex-col gap-0.5">
            <span class="inline-flex w-fit items-center gap-1 rounded bg-violet-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-violet-700 ring-1 ring-violet-500/30 dark:text-violet-300">
              <GraduationCap width="0.9em" height="0.9em" />
              Turma
            </span>
            <span class="truncate text-[11px] text-muted-foreground" title={group.ownerEmail ?? ""}>
              {group.ownerName ?? group.ownerEmail}
            </span>
          </div>
        ) : (
          <span class="inline-flex items-center gap-1 rounded bg-accent/40 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Institucional
          </span>
        )}
      </td>
      <td class="px-3 py-2">
        {group.companyName ? (
          <span class="inline-flex items-center gap-1 text-[11px]">
            <Building2 width="0.85em" height="0.85em" className="text-muted-foreground" />
            {group.companyName}
          </span>
        ) : (
          <span class="text-[11px] italic text-muted-foreground/70">Plataforma</span>
        )}
      </td>
      <td class="px-3 py-2">
        <span class="inline-flex items-center gap-1 rounded bg-accent/50 px-1.5 py-0.5 text-[11px] font-bold tabular-nums">
          <Users width="0.9em" height="0.9em" />
          {group.memberCount}
        </span>
      </td>
      <td class="px-3 py-2">
        <span class="inline-flex items-center gap-1 rounded bg-accent/50 px-1.5 py-0.5 text-[11px] font-bold tabular-nums">
          <Database width="0.9em" height="0.9em" />
          {group.bankCount}
        </span>
      </td>
      <td class="px-3 py-2">
        {group.tags.length === 0 ? (
          <span class="text-xs italic text-muted-foreground/70">—</span>
        ) : (
          <div class="flex flex-wrap gap-1">
            {group.tags.slice(0, 3).map((t) => (
              <span
                key={t}
                class="inline-flex items-center gap-0.5 rounded bg-violet-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-violet-700 ring-1 ring-violet-500/20 dark:text-violet-300"
              >
                <Tag width="0.8em" height="0.8em" />
                {t}
              </span>
            ))}
            {group.tags.length > 3 && (
              <span class="text-[10px] text-muted-foreground">
                +{group.tags.length - 3}
              </span>
            )}
          </div>
        )}
      </td>
      <td class="px-3 py-2 text-right">
        <div class="inline-flex items-center gap-1">
          <button
            type="button"
            onClick={() => openMembers(group.id)}
            class="rounded-md border border-sky-500/40 bg-sky-500/10 p-1.5 text-xs text-sky-700 hover:bg-sky-500/20 dark:text-sky-300"
            title="Membros da turma"
            aria-label="Membros da turma"
          >
            <UsersRound width="0.9em" height="0.9em" />
          </button>
          <button
            type="button"
            onClick={() => openBanks(group.id)}
            class="rounded-md border border-violet-500/40 bg-violet-500/10 p-1.5 text-xs text-violet-700 hover:bg-violet-500/20 dark:text-violet-300"
            title="Bancos da turma"
            aria-label="Bancos da turma"
          >
            <Database width="0.9em" height="0.9em" />
          </button>
          <button
            type="button"
            onClick={() => openEdit(group.id)}
            class="rounded-md border border-border bg-background p-1.5 text-xs hover:bg-accent"
            title="Editar turma"
          >
            <Pencil width="0.9em" height="0.9em" />
          </button>
          <button
            type="button"
            onClick={() => (deleting.value = group)}
            class="rounded-md border border-destructive/30 bg-destructive/5 p-1.5 text-xs text-destructive hover:bg-destructive/10"
            title="Excluir turma"
          >
            <Trash2 width="0.9em" height="0.9em" />
          </button>
        </div>
      </td>
    </tr>
  );
}

// ---------- TagsInput (copiado do padrão de Usuários/Bancos) ----------

function TagsInput({
  value,
  onChange
}: {
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const [draft, setDraft] = useState("");
  function commit(raw: string) {
    const s = raw.trim().toLowerCase();
    if (!s) return;
    if (value.includes(s)) {
      setDraft("");
      return;
    }
    onChange([...value, s].slice(0, 50));
    setDraft("");
  }
  return (
    <div class="rounded-md border border-border bg-background p-1.5">
      <div class="flex flex-wrap items-center gap-1">
        {value.map((t) => (
          <span
            key={t}
            class="inline-flex items-center gap-1 rounded bg-violet-500/10 px-1.5 py-0.5 text-[11px] font-semibold text-violet-700 ring-1 ring-violet-500/20 dark:text-violet-300"
          >
            <Tag width="0.8em" height="0.8em" />
            {t}
            <button
              type="button"
              onClick={() => onChange(value.filter((x) => x !== t))}
              class="opacity-60 hover:opacity-100"
              aria-label={`Remover ${t}`}
            >
              <X width="0.85em" height="0.85em" />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={draft}
          placeholder="adicionar tag (Enter)"
          onInput={(e) => setDraft((e.target as HTMLInputElement).value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              commit(draft);
            } else if (e.key === "Backspace" && !draft && value.length > 0) {
              onChange(value.slice(0, -1));
            }
          }}
          onBlur={() => commit(draft)}
          class="flex-1 min-w-[140px] bg-transparent px-1.5 py-0.5 text-xs outline-none"
        />
      </div>
    </div>
  );
}

// ---------- MembersPicker: lista de users selecionáveis ----------

function OwnerPicker({
  selected,
  onChange
}: {
  selected: string;
  onChange: (next: string) => void;
}) {
  const [q, setQ] = useState("");
  const [picking, setPicking] = useState(false);
  const users = allUsers.value ?? [];
  // Prioriza professores (usuários com role teacher em qualquer company),
  // mas mostra todos como fallback.
  const teachers = useMemo(
    () => users.filter((u) => u.companies.some((c) => c.role === "teacher")),
    [users]
  );
  const candidates = teachers.length > 0 ? teachers : users;
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return candidates.slice(0, 30);
    return candidates
      .filter((u) =>
        `${u.email} ${u.name} ${u.apelido ?? ""}`.toLowerCase().includes(t)
      )
      .slice(0, 30);
  }, [candidates, q]);
  const selUser = users.find((u) => u.id === selected);

  if (!picking) {
    return (
      <div class="flex items-center justify-between gap-2 rounded-md border border-border bg-background/50 px-3 py-2 text-sm">
        <div class="min-w-0 flex-1">
          {selUser ? (
            <>
              <div class="flex items-center gap-1 truncate font-semibold">
                <GraduationCap
                  width="0.9em"
                  height="0.9em"
                  className="text-violet-600 dark:text-violet-300"
                />
                {selUser.name}
              </div>
              <div class="truncate font-mono text-[11px] text-muted-foreground">
                {selUser.email}
              </div>
            </>
          ) : (
            <span class="text-xs italic text-muted-foreground">
              Sem dono — turma institucional
            </span>
          )}
        </div>
        <div class="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={() => setPicking(true)}
            class="rounded-md border border-border bg-background px-2 py-1 text-[11px] font-semibold hover:bg-accent"
          >
            {selUser ? "Trocar" : "Definir"}
          </button>
          {selUser && (
            <button
              type="button"
              onClick={() => onChange("")}
              title="Limpar dono"
              aria-label="Limpar dono"
              class="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
            >
              <X width="0.9em" height="0.9em" />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div class="space-y-2">
      <div class="relative">
        <Search
          width="1em"
          height="1em"
          className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
        />
        <input
          type="search"
          value={q}
          onInput={(e) => setQ((e.target as HTMLInputElement).value)}
          placeholder="Buscar professor por email, nome…"
          autoFocus
          class="w-full rounded-md border border-border bg-background pl-8 pr-3 py-1.5 text-sm outline-none focus:border-primary"
        />
      </div>
      <p class="text-[10px] text-muted-foreground/70">
        {teachers.length > 0
          ? `Mostrando ${filtered.length} de ${teachers.length} professor${teachers.length !== 1 ? "es" : ""}.`
          : `Nenhum professor cadastrado — mostrando todos os ${users.length} usuários.`}
      </p>
      <div class="max-h-[220px] overflow-y-auto rounded-md border border-border bg-background">
        {filtered.length === 0 ? (
          <p class="p-3 text-center text-xs italic text-muted-foreground">
            Nenhum usuário encontrado.
          </p>
        ) : (
          <ul class="divide-y divide-border">
            {filtered.map((u) => {
              const isSel = selected === u.id;
              return (
                <li
                  key={u.id}
                  class={`flex cursor-pointer items-center gap-2 px-3 py-1.5 text-xs transition-colors ${
                    isSel ? "bg-primary/10" : "hover:bg-accent/40"
                  }`}
                  onClick={() => {
                    onChange(u.id);
                    setPicking(false);
                  }}
                >
                  <input
                    type="radio"
                    checked={isSel}
                    readOnly
                    aria-label={`Selecionar ${u.email}`}
                    class="size-3.5"
                  />
                  <div class="min-w-0 flex-1">
                    <div class="truncate font-semibold">{u.name}</div>
                    <div class="truncate font-mono text-[10px] text-muted-foreground">
                      {u.email}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <button
        type="button"
        onClick={() => setPicking(false)}
        class="text-[11px] text-muted-foreground hover:text-foreground underline"
      >
        Cancelar
      </button>
    </div>
  );
}

function MembersPicker({
  selected,
  onChange
}: {
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const [q, setQ] = useState("");
  const users = allUsers.value ?? [];
  const selectedSet = new Set(selected);
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return users.slice(0, 50);
    return users
      .filter((u) =>
        `${u.email} ${u.name} ${u.apelido ?? ""}`.toLowerCase().includes(term)
      )
      .slice(0, 50);
  }, [users, q]);

  return (
    <div class="space-y-2">
      <div class="flex items-center justify-between">
        <span class="text-xs font-semibold text-muted-foreground">
          {selected.length} membro{selected.length !== 1 ? "s" : ""} selecionado
          {selected.length !== 1 ? "s" : ""}
        </span>
        {selected.length > 0 && (
          <button
            type="button"
            onClick={() => onChange([])}
            class="text-[11px] text-muted-foreground hover:text-foreground underline"
          >
            Limpar
          </button>
        )}
      </div>
      <div class="relative">
        <Search
          width="1em"
          height="1em"
          className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
        />
        <input
          type="search"
          value={q}
          onInput={(e) => setQ((e.target as HTMLInputElement).value)}
          placeholder="Buscar usuário por email, nome…"
          class="w-full rounded-md border border-border bg-background pl-8 pr-3 py-1.5 text-sm outline-none focus:border-primary"
        />
      </div>
      <div class="max-h-[280px] overflow-y-auto rounded-md border border-border bg-background">
        {filtered.length === 0 ? (
          <p class="p-3 text-center text-xs italic text-muted-foreground">
            Nenhum usuário encontrado.
          </p>
        ) : (
          <ul class="divide-y divide-border">
            {filtered.map((u) => {
              const isSel = selectedSet.has(u.id);
              return (
                <li
                  key={u.id}
                  class={`flex cursor-pointer items-center gap-2 px-3 py-1.5 text-xs transition-colors ${
                    isSel ? "bg-primary/10" : "hover:bg-accent/40"
                  }`}
                  onClick={() => {
                    if (isSel) onChange(selected.filter((x) => x !== u.id));
                    else onChange([...selected, u.id]);
                  }}
                >
                  <Checkbox checked={isSel} disabled />
                  <div class="min-w-0 flex-1">
                    <div class="truncate font-semibold">{u.name}</div>
                    <div class="truncate font-mono text-[10px] text-muted-foreground">
                      {u.email}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

// ---------- CreateGroupModal ----------

function CreateGroupModal() {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [companyId, setCompanyId] = useState<string>("");
  const [ownerUserId, setOwnerUserId] = useState<string>("");
  const [tags, setTags] = useState<string[]>([]);
  const [members, setMembers] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    if (!name.trim()) {
      setErr("Nome obrigatório.");
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      await api.createGroup({
        name: name.trim(),
        description: description.trim() || null,
        companyId: companyId || null,
        ownerUserId: ownerUserId || null,
        tags,
        members
      });
      creating.value = false;
      await load();
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalShell title="Nova turma" onClose={() => (creating.value = false)}>
      <div class="space-y-4 p-5">
        <Field label="Nome *">
          <input
            type="text"
            value={name}
            onInput={(e) => setName((e.target as HTMLInputElement).value)}
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
            placeholder="ex: ENAMED 2026 — UNICID"
            autoFocus
          />
        </Field>
        <Field label="Descrição">
          <textarea
            value={description}
            onInput={(e) => setDescription((e.target as HTMLTextAreaElement).value)}
            rows={2}
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
            placeholder="Opcional. Notas sobre a turma."
          />
        </Field>
        <Field label="Organização (opcional)">
          <select
            value={companyId}
            onChange={(e) => setCompanyId((e.target as HTMLSelectElement).value)}
            aria-label="Organização da turma"
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
          >
            <option value="">— Plataforma (cross-tenant) —</option>
            {companies.value.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Dono (professor) — opcional">
          <OwnerPicker selected={ownerUserId} onChange={setOwnerUserId} />
          <p class="mt-1 text-[10px] text-muted-foreground">
            Defina um professor como dono pra marcar como "turma". Sem dono =
            turma institucional.
          </p>
        </Field>
        <Field label="Tags">
          <TagsInput value={tags} onChange={setTags} />
          <p class="mt-1 text-[10px] text-muted-foreground">
            Use o formato <span class="font-mono">namespace:valor</span>. Aplique aos
            membros depois pela ação "Aplicar tags aos membros".
          </p>
        </Field>
        <Field label="Membros iniciais">
          <MembersPicker selected={members} onChange={setMembers} />
        </Field>
        {err && (
          <div class="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
            {err}
          </div>
        )}
      </div>
      <ModalFooter>
        <button
          type="button"
          onClick={() => (creating.value = false)}
          class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent"
          disabled={saving}
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={saving || !name.trim()}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110 disabled:opacity-50"
        >
          {saving ? (
            <Loader2 width="1.1em" height="1.1em" className="animate-spin" />
          ) : (
            <UsersRound width="1.1em" height="1.1em" />
          )}
          Criar turma
        </button>
      </ModalFooter>
    </ModalShell>
  );
}

// ---------- EditGroupModal ----------

function EditGroupModal({ group }: { group: GroupDetail }) {
  const [name, setName] = useState(group.name);
  const [description, setDescription] = useState(group.description ?? "");
  const [companyId, setCompanyId] = useState<string>(group.companyId ?? "");
  const [ownerUserId, setOwnerUserId] = useState<string>(group.ownerUserId ?? "");
  const [tags, setTags] = useState<string[]>(group.tags);
  const initialMembers = group.members.map((m) => m.id);
  const [members, setMembers] = useState<string[]>(initialMembers);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  async function submit() {
    if (!name.trim()) {
      setErr("Nome obrigatório.");
      return;
    }
    setSaving(true);
    setErr(null);
    setInfo(null);
    try {
      await api.patchGroup(group.id, {
        name: name.trim(),
        description: description.trim() || null,
        companyId: companyId || null,
        ownerUserId: ownerUserId || null,
        tags,
        members
      });
      editing.value = null;
      await load();
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setSaving(false);
    }
  }

  async function applyTags() {
    if (tags.length === 0) {
      setErr("A turma não tem tags pra aplicar. Adicione antes de aplicar.");
      return;
    }
    setApplying(true);
    setErr(null);
    setInfo(null);
    try {
      // Salva primeiro (caso usuário tenha mexido em tags sem salvar)
      await api.patchGroup(group.id, { tags });
      const res = await api.applyGroupTags(group.id);
      setInfo(
        `✓ Aplicadas ${res.tags} tag${res.tags !== 1 ? "s" : ""} em ${res.touched} usuário${
          res.touched !== 1 ? "s" : ""
        }.`
      );
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setApplying(false);
    }
  }

  async function syncGrants() {
    setSyncing(true);
    setErr(null);
    setInfo(null);
    try {
      // Garante que o estado atual do grupo está salvo antes de sincronizar
      // (members/bankIds podem ter mudado e ainda não persistido).
      await api.patchGroup(group.id, { members });
      const res = await api.syncGroupGrants(group.id);
      if (res.message) {
        setInfo(`✓ ${res.message}`);
      } else {
        setInfo(
          `✓ ${res.granted} grant${res.granted !== 1 ? "s" : ""} novo${
            res.granted !== 1 ? "s" : ""
          } criado${res.granted !== 1 ? "s" : ""} (${res.skipped} já existiam) — ${res.members} membro${
            res.members !== 1 ? "s" : ""
          } × ${res.banks} banco${res.banks !== 1 ? "s" : ""}.`
        );
      }
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setSyncing(false);
    }
  }

  return (
    <ModalShell title={`Editar turma: ${group.name}`} onClose={() => (editing.value = null)}>
      <div class="space-y-4 p-5">
        <Field label="Nome *">
          <input
            type="text"
            value={name}
            onInput={(e) => setName((e.target as HTMLInputElement).value)}
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </Field>
        <Field label="Descrição">
          <textarea
            value={description}
            onInput={(e) => setDescription((e.target as HTMLTextAreaElement).value)}
            rows={2}
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </Field>
        <Field label="Organização (opcional)">
          <select
            value={companyId}
            onChange={(e) => setCompanyId((e.target as HTMLSelectElement).value)}
            aria-label="Organização da turma"
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
          >
            <option value="">— Plataforma (cross-tenant) —</option>
            {companies.value.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Dono (professor) — opcional">
          <OwnerPicker selected={ownerUserId} onChange={setOwnerUserId} />
          <p class="mt-1 text-[10px] text-muted-foreground">
            {ownerUserId
              ? "Marcado como turma. O dono é o professor responsável."
              : "Sem dono = turma institucional (criada pelo admin)."}
          </p>
        </Field>
        <div>
          <div class="mb-1.5 flex items-center justify-between">
            <span class="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Tags
            </span>
            <button
              type="button"
              onClick={applyTags}
              disabled={applying || tags.length === 0}
              class="inline-flex items-center gap-1 rounded-md border border-violet-500/30 bg-violet-500/10 px-2 py-1 text-[10px] font-bold text-violet-700 hover:bg-violet-500/20 disabled:opacity-50 dark:text-violet-300"
              title="Faz merge das tags da turma nas tags dos membros (idempotente)"
            >
              {applying ? (
                <Loader2 width="1em" height="1em" className="animate-spin" />
              ) : (
                <Sparkles width="1em" height="1em" />
              )}
              Aplicar nos membros
            </button>
          </div>
          <TagsInput value={tags} onChange={setTags} />
        </div>
        <Field label="Membros">
          <MembersPicker selected={members} onChange={setMembers} />
        </Field>

        {/* Sincronizar grants — materializa bankIds × members em bank_user_grants
            (dá acesso direto aos alunos da turma aos bancos atribuídos).
            Idempotente: não toca grants existentes (preserva canTakeExam manual). */}
        <div class="rounded-md border border-violet-500/30 bg-violet-500/5 p-3">
          <div class="flex items-start gap-3">
            <Link2
              width="1.2em"
              height="1.2em"
              className="mt-0.5 shrink-0 text-violet-600 dark:text-violet-300"
            />
            <div class="min-w-0 flex-1">
              <div class="text-xs font-bold text-violet-700 dark:text-violet-300">
                Sincronizar acesso aos bancos da turma
              </div>
              <p class="mt-0.5 text-[11px] text-muted-foreground">
                Cria grants diretos pra cada (membro × banco) desta turma.
                {group.bankCount === 0
                  ? " A turma ainda não tem bancos atribuídos."
                  : ` ${group.memberCount} membro${
                      group.memberCount !== 1 ? "s" : ""
                    } × ${group.bankCount} banco${
                      group.bankCount !== 1 ? "s" : ""
                    } = até ${group.memberCount * group.bankCount} grant${
                      group.memberCount * group.bankCount !== 1 ? "s" : ""
                    } criado${group.memberCount * group.bankCount !== 1 ? "s" : ""}.`}
              </p>
            </div>
            <button
              type="button"
              onClick={syncGrants}
              disabled={syncing || group.bankCount === 0}
              class="inline-flex shrink-0 items-center gap-1 rounded-md border border-violet-500/40 bg-violet-500/15 px-2 py-1 text-[11px] font-bold text-violet-700 hover:bg-violet-500/25 disabled:opacity-50 dark:text-violet-300"
            >
              {syncing ? (
                <Loader2 width="1em" height="1em" className="animate-spin" />
              ) : (
                <Link2 width="1em" height="1em" />
              )}
              Sincronizar
            </button>
          </div>
        </div>

        <div class="rounded-md border border-border bg-card/30 px-3 py-2 text-[11px] text-muted-foreground">
          <div class="flex items-center justify-between">
            <span>Bancos referenciados: {group.bankCount}</span>
            <span>Criado por: {group.createdByEmail ?? "—"}</span>
          </div>
          {group.ownerEmail && (
            <div class="mt-1">
              Dono: <span class="font-semibold text-foreground">{group.ownerName}</span>{" "}
              <span class="font-mono">{group.ownerEmail}</span>
            </div>
          )}
          <div class="mt-1">Criado em {new Date(group.createdAt).toLocaleString()}</div>
        </div>
        {err && (
          <div class="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
            {err}
          </div>
        )}
        {info && (
          <div class="rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-xs text-emerald-700 dark:text-emerald-300">
            {info}
          </div>
        )}
      </div>
      <ModalFooter>
        <button
          type="button"
          onClick={() => (editing.value = null)}
          class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent"
          disabled={saving}
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={saving || !name.trim()}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110 disabled:opacity-50"
        >
          {saving ? (
            <Loader2 width="1.1em" height="1.1em" className="animate-spin" />
          ) : (
            <Save width="1.1em" height="1.1em" />
          )}
          Salvar
        </button>
      </ModalFooter>
    </ModalShell>
  );
}

// ---------- DeleteGroupModal ----------

function DeleteGroupModal({ group }: { group: GroupListRow }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function confirm() {
    setBusy(true);
    setErr(null);
    try {
      await api.deleteGroup(group.id);
      deleting.value = null;
      await load();
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <ModalShell title="Excluir turma" onClose={() => (deleting.value = null)}>
      <div class="space-y-3 p-5">
        <p class="text-sm">
          Tem certeza que quer excluir a turma <strong>{group.name}</strong>?
        </p>
        <p class="text-xs text-muted-foreground">
          A associação com os {group.memberCount} membro{group.memberCount !== 1 ? "s" : ""}{" "}
          será removida. Tags já propagadas continuam nos usuários (você pode removê-las
          manualmente).
        </p>
        {err && (
          <div class="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
            {err}
          </div>
        )}
      </div>
      <ModalFooter>
        <button
          type="button"
          onClick={() => (deleting.value = null)}
          class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent"
          disabled={busy}
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={confirm}
          disabled={busy}
          class="inline-flex items-center gap-1.5 rounded-md bg-destructive px-3 py-1.5 text-xs font-bold text-white hover:brightness-110 disabled:opacity-50"
        >
          {busy ? (
            <Loader2 width="1.1em" height="1.1em" className="animate-spin" />
          ) : (
            <Trash2 width="1.1em" height="1.1em" />
          )}
          Excluir
        </button>
      </ModalFooter>
    </ModalShell>
  );
}

// ---------- Helpers ----------

function Field({ label, children }: { label: string; children: any }) {
  return (
    <label class="block">
      <span class="mb-1 block text-xs font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}

function ModalShell({
  title,
  onClose,
  children
}: {
  title: string;
  onClose: () => void;
  children: any;
}) {
  return (
    <div
      class="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        class="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg border border-border bg-background shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div class="flex items-center justify-between border-b border-border px-5 py-3">
          <h2 class="text-base font-bold">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            class="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="Fechar"
          >
            <X width="1.1em" height="1.1em" />
          </button>
        </div>
        <div class="flex-1 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

function ModalFooter({ children }: { children: any }) {
  return (
    <div class="flex items-center justify-end gap-2 border-t border-border bg-card/40 px-5 py-3">
      {children}
    </div>
  );
}
